import { useEffect, useState } from "react";
import { fetchParcel, type ParcelFlag } from "./api";
import { renderFlag } from "./copy";
import type { ParcelClick } from "./MapView";

// Bottom-sheet detail card. Pulls everything from the PMTiles click context
// (no extra HTTP) when available; falls back to fetchParcel() for the
// seed-only / search-driven path.

export function BottomSheet({ click, onClose }: { click: ParcelClick | null; onClose: () => void }) {
  const [parcel, setParcel] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!click) { setParcel(null); setErr(null); return; }
    // Prefer the click context — it's the source of truth for PMTiles data.
    if (click.props && Object.keys(click.props).length > 1) {
      setParcel({ folio: click.folio, ...click.props });
      setErr(null);
      return;
    }
    setParcel(null);
    fetchParcel(click.folio).then(setParcel).catch((e) => setErr(String(e)));
  }, [click]);

  if (!click) return null;
  const status: string | undefined = parcel?.density_status ?? parcel?.densityStatus;
  const upzoneDelta = parcel?.upzoneDelta ?? parcel?.upzone_delta;
  const zoningCode = parcel?.zoning_code ?? parcel?.zoningCode;
  const municipality = parcel?.municipality;
  const asOf = parcel?.asOfDate ?? parcel?.as_of_date ?? new Date().toISOString().slice(0,10);
  const rulesetVersion = parcel?.rulesetVersion ?? parcel?.ruleset_version ?? "LLA-2025-SB1730";
  const isLla = parcel?.liveLocalFlag ?? parcel?.live_local_flag ?? false;
  const opportunityScore = parcel?.opportunityScore ?? parcel?.opportunity_score ?? 0;
  const flag = renderFlag({ asOf, rulesetVersion, intent: isLla ? "flag" : "signal" });

  return (
    <div className="ps-sheet" role="dialog" aria-label="Parcel detail">
      <button className="ps-sheet__close" onClick={onClose} aria-label="Close">×</button>
      {err && <p className="ps-sheet__err">Failed to load: {err}</p>}
      {parcel && (
        <>
          <header className="ps-sheet__head">
            <div className="ps-sheet__folio">{parcel.folio}</div>
            <div
              className="ps-sheet__score"
              style={{ background: scoreBg(opportunityScore) }}
            >
              {Math.round(opportunityScore)}
            </div>
          </header>
          {parcel.address && <p className="ps-sheet__addr">{parcel.address}</p>}

          <dl className="ps-sheet__metrics">
            <div>
              <dt>Upzone Δ</dt>
              <dd>{renderUpzone(upzoneDelta, status)}</dd>
            </div>
            <div>
              <dt>Teardown</dt>
              <dd className="ps-sheet__unavail">unavailable — v1.1</dd>
            </div>
            <div>
              <dt>Zoning</dt>
              <dd>{zoningCode ?? "—"}{municipality && municipality !== "UNINCORPORATED" ? ` · ${municipality}` : ""}</dd>
            </div>
            <div>
              <dt>Live Local</dt>
              <dd className="ps-sheet__flagphrase">{flag.phrase}</dd>
            </div>
            {parcel.year_built && <div><dt>Built</dt><dd>{parcel.year_built}</dd></div>}
          </dl>

          {status && status !== "ok" && (
            <p className="ps-sheet__statusnote">{statusNote(status)}</p>
          )}

          <p className="ps-sheet__disclaimer" role="note">{flag.disclaimer}</p>
          <p className="ps-sheet__staleness">{flag.staleness}</p>
        </>
      )}
    </div>
  );
}

function renderUpzone(v: number | null | undefined, status?: string): string {
  if (status && status !== "ok") return "—";
  if (v === null || v === undefined) return "—";
  return `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)} u/ac`;
}

function statusNote(status: string): string {
  switch (status) {
    case "municipal_code_not_yet_encoded":
      return "Municipal parcel — code not yet encoded in v1. Raw zoning shown above; score reserved for v1.1.";
    case "density_uncomputable_no_lot_size":
      return "Density uncomputable — no lot size in public data (typical for condos and common areas).";
    case "no_zoning_data":
      return "No zoning polygon contains this parcel's centroid — score unavailable.";
    case "no_by_right_residential":
      return "Zoning class has no by-right residential density. Live Local Act may unlock — verify with counsel.";
    default:
      return "";
  }
}

function scoreBg(s: number): string {
  if (s >= 80) return "#ed1f1f";
  if (s >= 60) return "#ed8a2e";
  if (s >= 40) return "#e3c83a";
  if (s >= 20) return "#3aa66f";
  if (s > 0)   return "#3a6fb8";
  return "#3a3a3a";
}
